/* eslint-disable max-len */
import React, { FC, useEffect, useState } from 'react';
import { ArrowForward as Arrow, Edit, Info, MoreVert as MoreVertIcon } from '@mui/icons-material';
import { IconButton, Menu, MenuItem } from '@mui/material';
import moment from 'moment';

import imgPhone from '@/assets/icons/controll/whatsapp.svg';
import CopyIcon from '@/assets/icons/generals/copywhite.svg';

import {
  formatDateToShow,
  formatHourToShow,
  numberToCurrency,
  onlyNumbers,
  removeSecondsHourToShow,
} from '@/utils/Formatter';
import {
  translateBlockingReason,
  translatePaymentStatus,
  translateReservationStatus,
} from '@/utils/Translator';

import { getAddressById } from '@/services/Address/request';
import { getFilesByFileUid } from '@/services/NFlist/request';
import { patchReservation } from '@/services/Reservation/request';
import { Reservation, ReservationStatus } from '@/services/Reservation/types';

import { useReservation } from '@/context/ReservationContext';
import { useUser } from '@/context/UserContext';

import { useModal } from '@/hooks/ModalHook/useModal';
import { useReservationForm } from '@/hooks/ReservationHook/useReservationForm';

import CancellReservation from '@/components/Calendar/Reservation/ModalCancell/CancellReservation';
import ExtendReservation from '@/components/Calendar/Reservation/ModalExtendReserve';
import ModalReallocateReservation from '@/components/Calendar/Reservation/ModalReallocateReservation/ModalReallocateReservation';
import { FormButton } from '@/components/index';
import Tooltip from '@/components/Tooltip';

import {
  AddressContainer,
  BarLabel,
  ButtonConfirmUpdateStatus,
  ButtonsContainer,
  ButtonStyled,
  CheckContainer,
  Container,
  Contract,
  CopyButton,
  CopyContainer,
  Description,
  DetailsContainer,
  FinanceContainer,
  FinanceContent,
  FormContainer,
  FormContent,
  IconContainer,
  InfoContainer,
  InfoIcon,
  LastSmallText,
  MediumTextBold,
  ModalUpdateReservationStatus,
  NotesContainer,
  PhoneContainer,
  PropertyImage,
  RadioButton,
  Separator,
  SmallText,
  SmallTextBold,
  SmallTextNote,
  Space,
  TitleContainer,
  Wrapper,
} from './styles';

interface Props {
  props?: object;
  bookings?: Reservation | any;
  totalDays: number;
  propertyAddressId: number;
}

const ITEM_HEIGHT = 48;

const GeneralForm: FC<Props> = ({ totalDays, bookings, propertyAddressId }) => {
  const { userInformation } = useUser();
  const roles = userInformation?.roles || [];
  const { handleModalActive } = useModal();
  const { handleResetDataGrouped } = useReservationForm();
  const { reservationData, setReservationData } = useReservation();
  const client = `${reservationData?.guest?.user?.first_name || ''} ${reservationData?.guest?.user?.last_name || ''}`;
  const phoneNumber = reservationData?.guest?.user?.phone_number1 || '';
  const adultGuests = reservationData?.adult_guest_quantity || 0;
  const childrenGuests = reservationData?.child_guest_quantity || 0;
  const createdAtDate = formatDateToShow(reservationData?.created_at) || '';
  const createdAtHour = formatHourToShow(reservationData?.created_at) || '';
  const checkInDate = reservationData?.check_in_date || '';
  const checkOutDate = reservationData?.check_out_date || '';
  const netCleaningFee = reservationData.net_cleaning_fee;
  const { code, comment, paymentStatus } = reservationData;
  const checkInTime = reservationData?.check_in_time || '';
  const checkOutTime = reservationData?.check_out_time || '';
  const totalPrice = numberToCurrency(Number(reservationData?.daily_net_value)) || 0;
  const ota = reservationData?.listing?.ota?.name || '';
  const bondAmount = numberToCurrency(Number(reservationData?.listing?.property?.bond_amount)) || 0;
  const propertyCode = reservationData?.listing?.property?.code || 0;
  const guestName = reservationData?.guest?.user?.first_name || '';
  const wasContacted = reservationData?.was_contacted_checkin;

  const checkouts = bookings
    .filter((booking: Reservation) => booking.id !== reservationData.id)
    .map((checkout: Reservation) => checkout.check_out_date);

  const hasHotbed = checkouts.some((date: string) => date === reservationData?.check_in_date);
  const [addressProperty, setAddressProperty] = useState<string>('');
  const [extendModal, setExtendModal] = useState<boolean>(false);
  const [cancellModal, setCancellModal] = useState<boolean>(false);
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const [contractLink, setContractLink] = useState<string>('');
  const [openModalStatus, setOpenModalStatus] = useState<boolean>(false);
  const [openButtonConfirm, setOpenButtonConfirm] = useState<boolean>(false);
  const [reservationStatus, setReservationStatus] = useState('');

  const open = Boolean(anchorEl);

  const [openModalReallocateReservation, setOpenModalReallocateReservation] =
    useState<boolean>(false);

  const getAddress = async () => {
    const address = await getAddressById(propertyAddressId);
    if (address) {
      const { street, number, neighborhood, complement, city, state, country } = address;

      setAddressProperty(
        `${street} ${number}, ${neighborhood} - ${complement} ${city} - ${state} - ${country}, CEP: ${address.postal_code}`,
      );
    } else {
      setAddressProperty('');
    }
  };

  useEffect(() => {
    getAddress();
    return () => {
      setAddressProperty('');
    };
  }, []);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleOpenConfirmationModal = (e: boolean | any) => {
    setCancellModal(e);
  };

  const handleExtendModal = (e: boolean | any) => {
    setExtendModal(e);
  };

  let commentSplitted = comment?.split('; cancellation:');
  commentSplitted = commentSplitted?.filter((txt: string) => txt !== '; cancellation:');

  const reservationCopyInformation = `Código de reserva: ${code}\nCódigo do imóvel: ${propertyCode}\nData de Check-in: ${checkInDate}\nData de Check-out: ${checkOutDate}\nNome do hóspede: ${guestName}`;

  const options = [
    {
      icon: Edit,
      description: 'Atualizar status da reserva',
      function: () => setOpenModalStatus(true),
    },
    // {
    //   icon: iconSendContact,
    //   description: 'Realocar reserva',
    //   onClick: () => setOpenModalReallocateReservation(true),
    // },
  ];

  function openContract(urlContract: string) {
    window.open(urlContract, '_blank');
  }

  async function getContract() {
    const result = await getFilesByFileUid(reservationData?.monthly_contract);
    setContractLink(result?.url || '');
  }

  useEffect(() => {
    if (reservationData?.is_monthly) {
      getContract();
    }
  }, []);

  async function handleUpdateStatus(type: string) {
    const dataRequest = {
      check_in_date: checkInDate,
      check_out_date: checkOutDate,
      comment,
      conciliada: reservationData.conciliada || false,
      status: type as ReservationStatus,
    };

    await patchReservation(reservationData.id, dataRequest);
    setReservationData({ ...reservationData, status: type });
    handleResetDataGrouped();
  }

  function handleConfirmUpdate() {
    handleUpdateStatus(reservationStatus);
    setOpenButtonConfirm(false);
    setOpenModalStatus(false);
  }

  function handleCheckRadio(type: string) {
    setOpenButtonConfirm(true);
    setReservationStatus(type);
  }

  const [hostCanCancelReservation, setHostCanCancelReservation] = useState(false);

  useEffect(() => {
    if (roles.includes('Host')) {
      const hostCanBlockReasons =
        reservationData.blocking_reason === 'Host' ||
        reservationData.blocking_reason === 'Maintenance' ||
        reservationData.blocking_reason === 'Cleaning' ||
        reservationData.blocking_reason === 'Owner use';

      if (hostCanBlockReasons && reservationData.is_blocking === true) {
        setHostCanCancelReservation(true);
      } else {
        setHostCanCancelReservation(false);
      }
    } else {
      setHostCanCancelReservation(true);
    }
  }, [roles, reservationData.blocking_reason]);

  return (
    <>
      <Container role={userInformation?.main_role || ''}>
        {extendModal === true || cancellModal === true ? (
          <>
            {cancellModal === true ? (
              <>
                <CancellReservation
                  reservationData={reservationData}
                  onClose={handleOpenConfirmationModal}
                />
              </>
            ) : (
              <ExtendReservation onClose={handleExtendModal} />
            )}
          </>
        ) : (
          <FormContainer
            onContextMenu={(event): void => {
              event.stopPropagation();
            }}
          >
            <FormContent>
              <TitleContainer>
                <Wrapper>
                  {!reservationData?.is_blocking && <MediumTextBold>{client}</MediumTextBold>}
                  <div>
                    <SmallText>{`${adultGuests} adultos . ${totalDays - 1} diárias`}</SmallText>
                    <LastSmallText>. {roles.includes('Host') ? '-' : totalPrice}</LastSmallText>
                  </div>
                </Wrapper>
                <Wrapper>
                  <ButtonStyled
                    type="button"
                    reservationStatus={reservationData?.status}
                    blocked={
                      (reservationData?.is_blocking && reservationData?.status !== 'Canceled') ||
                      false
                    }
                  >
                    <SmallTextBold>
                      {!reservationData?.is_blocking
                        ? translateReservationStatus(reservationData?.status)
                        : 'Bloqueada'}
                    </SmallTextBold>
                  </ButtonStyled>
                  {reservationData?.is_blocking ? (
                    <SmallText>
                      {`Motivo: ${translateBlockingReason(reservationData?.blocking_reason)}`}
                    </SmallText>
                  ) : (
                    <PhoneContainer
                      onClick={() =>
                        window.open(
                          `https://api.whatsapp.com/send?phone=${onlyNumbers(phoneNumber)}`,
                          '_blank',
                        )
                      }
                    >
                      <img src={imgPhone} alt="Phone" />
                      <SmallText>{phoneNumber}</SmallText>
                    </PhoneContainer>
                  )}
                </Wrapper>
              </TitleContainer>

              <AddressContainer>
                <SmallText>
                  <strong>{`${reservationData?.listing?.property?.code} `}</strong>
                  {addressProperty}
                </SmallText>
                <PropertyImage backgroundImage="" />
              </AddressContainer>

              <CheckContainer>
                <Wrapper>
                  <SmallTextBold>Check-in</SmallTextBold>
                  <SmallText>{moment(checkInDate).format('DD/MMM/YYYY')}</SmallText>
                </Wrapper>
                <Arrow />
                <Wrapper>
                  <SmallTextBold>Check-out</SmallTextBold>
                  <SmallText>{moment(checkOutDate).format('DD/MMM/YYYY')}</SmallText>
                </Wrapper>
                {hasHotbed && (
                  <Wrapper>
                    <ButtonStyled type="button" reservationStatus={'Pre-booking'}>
                      <SmallTextBold>Cama quente</SmallTextBold>
                    </ButtonStyled>
                  </Wrapper>
                )}
              </CheckContainer>

              <DetailsContainer>
                <Wrapper>
                  <SmallText>Código da reserva</SmallText>
                  <SmallText>{code}</SmallText>
                </Wrapper>
                <Wrapper>
                  <SmallText>Total de noites</SmallText>
                  <SmallText>{totalDays - 1}</SmallText>
                </Wrapper>
                <Wrapper>
                  <InfoContainer>
                    <SmallText>Valor total líquido</SmallText>
                    <Tooltip text="Valor já com o desconto da taxa de OTA">
                      <InfoIcon />
                    </Tooltip>
                  </InfoContainer>
                  <SmallText>{totalPrice}</SmallText>
                </Wrapper>
                <Wrapper>
                  <SmallText>Valor líquido da taxa de limpeza</SmallText>
                  <SmallText>R$ {netCleaningFee}</SmallText>
                </Wrapper>
                {checkInTime && (
                  <Wrapper>
                    <SmallText>Horário check-in</SmallText>
                    <SmallText>
                      {reservationData?.is_blocking
                        ? '00:00'
                        : removeSecondsHourToShow(checkInTime)}
                    </SmallText>
                  </Wrapper>
                )}
                {checkOutTime && (
                  <Wrapper>
                    <SmallText>Horário check-out</SmallText>
                    <SmallText>
                      {reservationData?.is_blocking
                        ? '00:00'
                        : removeSecondsHourToShow(checkOutTime)}
                    </SmallText>
                  </Wrapper>
                )}
                <Wrapper>
                  <SmallText>Data de criação</SmallText>
                  <SmallText>{`${createdAtDate} ${removeSecondsHourToShow(createdAtHour)}`}</SmallText>
                </Wrapper>
                {!reservationData?.is_blocking && (
                  <Wrapper>
                    <SmallText>Origem da reserva</SmallText>
                    <SmallText>{ota}</SmallText>
                  </Wrapper>
                )}
                <Wrapper>
                  <SmallText>N°. de hóspedes</SmallText>
                  <SmallText>{`${adultGuests} adulto(s), ${childrenGuests} criança(s) ${reservationData?.has_pet > 0 ? `e ${reservationData.has_pet} Pet(s)` : ''}`}</SmallText>
                </Wrapper>
                {reservationData?.is_monthly && (
                  <Wrapper>
                    <SmallText data-cy="contract">Contrato</SmallText>
                    <Contract type="button" onClick={() => openContract(contractLink)}>
                      Ver contrato
                    </Contract>
                  </Wrapper>
                )}
                <Wrapper>
                  <SmallText>Hóspede já contatado?</SmallText>
                  <SmallText>{wasContacted === true ? 'Sim' : 'Não'}</SmallText>
                </Wrapper>
              </DetailsContainer>
              <CopyContainer>
                <CopyButton
                  onClick={() => navigator.clipboard.writeText(reservationCopyInformation)}
                  type="button"
                >
                  <img src={CopyIcon} alt="icone de copiar" />
                  Copiar informações da reserva
                </CopyButton>
              </CopyContainer>
              <NotesContainer>
                <BarLabel>Notas</BarLabel>
                {reservationData?.is_blocking && commentSplitted[0]?.length >= 1 && (
                  <>
                    <Space />
                    <SmallTextBold className="notes">Imóvel bloqueado</SmallTextBold>
                    <SmallTextNote
                      disabled
                      rows={commentSplitted[0]?.length > 300 ? 10 : 5}
                      cols={40}
                    >
                      {commentSplitted[0]}
                    </SmallTextNote>
                  </>
                )}
                {reservationData?.is_blocking && reservationData?.status === 'Canceled' && (
                  <Separator />
                )}
                {reservationData?.status === 'Canceled' && (
                  <>
                    {commentSplitted[1]?.length > 1 && (
                      <>
                        <Space />
                        <SmallTextBold className="notes">
                          {reservationData?.is_blocking
                            ? 'Bloqueio cancelado'
                            : 'Reserva cancelada'}
                        </SmallTextBold>
                        <SmallTextNote
                          disabled
                          rows={commentSplitted[1]?.length > 300 ? 10 : 3}
                          cols={40}
                        >
                          {commentSplitted[1]}
                        </SmallTextNote>
                      </>
                    )}
                  </>
                )}
              </NotesContainer>

              {!reservationData?.is_blocking && !roles.includes('Host') && (
                <FinanceContainer>
                  <BarLabel>Financeiro</BarLabel>
                  <FinanceContent>
                    <div>
                      <Wrapper>
                        <SmallText>Status pagamento</SmallText>
                        <SmallText>{translatePaymentStatus(paymentStatus)}</SmallText>
                      </Wrapper>
                      <Wrapper>
                        <SmallText>Valor caução</SmallText>
                        <SmallText>{bondAmount}</SmallText>
                      </Wrapper>
                    </div>
                    <button type="button" onClick={() => handleModalActive(2)}>
                      Mais informações
                    </button>
                  </FinanceContent>
                </FinanceContainer>
              )}
            </FormContent>

            <>
              <ButtonsContainer>
                {reservationData?.status === 'Canceled' ? (
                  <>
                    {reservationData?.is_blocking ? (
                      <FormButton disable customColor="grey" type="button">
                        Bloqueio cancelado
                      </FormButton>
                    ) : (
                      !roles.includes('Host') && (
                        <FormButton disable customColor="grey" type="button">
                          Reserva cancelada
                        </FormButton>
                      )
                    )}
                  </>
                ) : (
                  <>
                    {
                      <>
                        {/* {
                            reservationData?.is_blocking ? (
                              <FormButton type="button" onClick={() => handleOpenConfirmationModal(true)}>Cancelar bloqueio</FormButton>
                            ) : (!roles.includes('Host') && (
                              <FormButton type="button" onClick={() => handleOpenConfirmationModal(true)}>Cancelar reserva</FormButton>
                            ))
                          }
                        {!roles.includes('Host') && (
                          (reservationData?.has_late_extension
                            && reservationData?.has_early_extension === true) ? (
                              <FormButton type="button" disable variant="outlined">Reserva já estendida</FormButton>
                            ) : (
                              <>
                                {
                                   reservationData?.is_blocking ? (
                                     <div />
                                   ) : (
                                     <>
                                       {
                                        (reservationData?.has_early_extension
                                          && reservationData?.is_early_extension
                                          && reservationData?.original_reservation != null)
                                            || (reservationData?.has_late_extension
                                              && reservationData?.is_late_extension) ? (
                                                <FormButton type="button" disable variant="outlined">Reserva já estendida</FormButton>
                                          ) : (
                                            <FormButton type="button" variant="outlined" onClick={() => handleExtendModal(false)}>Estender Reserva</FormButton>
                                          )
                                      }
                                     </>
                                   )
                                 }
                              </>
                            )
                        )} */}
                      </>
                    }
                  </>
                )}
                {hostCanCancelReservation && (
                  <>
                    <Tooltip text="Mais opções">
                      <IconButton
                        aria-label="more"
                        aria-controls="long-menu"
                        aria-haspopup="true"
                        onClick={handleClick}
                      >
                        <MoreVertIcon />
                      </IconButton>
                    </Tooltip>
                    <Menu
                      id="long-menu"
                      anchorEl={anchorEl}
                      keepMounted
                      open={open}
                      onClose={handleClose}
                      PaperProps={{
                        style: {
                          maxHeight: ITEM_HEIGHT * 4.5,
                          width: '32ch',
                        },
                      }}
                    >
                      {options.map((option) => (
                        <MenuItem
                          key={option.description}
                          selected={option.description === 'Pyxis'}
                          onClick={option.function}
                        >
                          {option?.icon && (
                            <IconContainer>
                              <option.icon
                                style={{
                                  color: '#acaca',
                                }}
                              />
                            </IconContainer>
                          )}
                          <Description>{option.description}</Description>
                        </MenuItem>
                      ))}
                    </Menu>
                  </>
                )}
              </ButtonsContainer>
            </>
            <Space />
            <Space />
            <Space />
          </FormContainer>
        )}
      </Container>
      <ModalUpdateReservationStatus openModal={openModalStatus}>
        {!openButtonConfirm ? (
          <>
            <div className="row">
              <Edit />
              <p>Alterar status</p>
            </div>
            <div className="row">
              <RadioButton
                type="radio"
                name="status"
                onChange={() => handleCheckRadio('Concluded')}
              />
              <p>Concluída</p>
            </div>
            <div className="row">
              <RadioButton
                type="radio"
                name="status"
                onChange={() => handleCheckRadio('Canceled')}
              />
              <p>Cancelada</p>
            </div>
            <div className="row">
              <RadioButton
                type="radio"
                name="status"
                onChange={() => handleCheckRadio('No-Show')}
              />
              <p>No Show</p>
            </div>
          </>
        ) : (
          <ButtonConfirmUpdateStatus onClick={() => handleConfirmUpdate()}>
            <Info /> Confirmar atualização do status para{' '}
            {translateReservationStatus(reservationStatus as ReservationStatus)}?
          </ButtonConfirmUpdateStatus>
        )}
      </ModalUpdateReservationStatus>
      <ModalReallocateReservation
        open={openModalReallocateReservation}
        onClose={setOpenModalReallocateReservation}
        reservationId={reservationData.id}
      />
    </>
  );
};

export default GeneralForm;
